// Shared helpers for native visual renderers: turn the [schemaRow, ...rows]
// jsonData + visualLayout.ColumnGroups into dimensions / measures the
// renderers can consume, with sensible fallbacks when groups aren't mapped.

import type { VisualJsonData, VisualLayout } from './types'
import { dropMovedColumnCopies, type GroupLike } from './layoutGroups'

/** First of the candidates that is a non-blank string, else undefined. */
function firstFilled(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (v == null) continue
    const s = String(v).trim()
    if (s) return s
  }
  return undefined
}

/** Title-case a raw result-set key for use as a heading:
 *  ``NET_SALES_VALUE`` → ``Net Sales Value``, ``netSalesValue`` → ``Net Sales
 *  Value``. Mirrors the designer's `prettify`, so a column that falls back to
 *  its DB name reads the same way it does in the Schema Designer instead of
 *  showing the raw SCREAMING_SNAKE alias. A name that already contains a space
 *  is a hand-written label and is left alone. */
export function humanizeColumnLabel(name: string): string {
  const s = String(name ?? '')
  if (!s || /\s/.test(s)) return s
  return s
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || s
}

export interface MappedColumn {
  name: string
  label: string
  type?: string
  // Raw ColumnGroup metadata, surfaced so renderers can match insight-bi's
  // display logic (formatting, aggregation, visibility, alignment).
  coltype?: string   // COLTYPE: 'NUMBER' | 'DATE' | 'CHAR' | 'T'
  format?: string    // FORMAT mask: IND/USA/EURO/9-mask, or 'Icon'
  pattern?: string   // PATTERN: date pattern (e.g. 'dd-MMM-yy')
  agg?: string       // aggregation: FUNCTION / WINDOWFUNCTION / DEFAULTFUNCTION
  hidden?: boolean   // HIDDEN column (kept out of the render)
  alignment?: string // 'left' | 'right'
  advFormat?: string // ADV_FORMAT: conditional formatting ('formula~JSON' [$ ...])
  calcExpression?: string // expression for a calculated (Presentation) column
  isCalc?: boolean   // CALC_TYPE === 'Presentation' (client-computed measure)
  // ── Per-column rich formatting + pivot aggregation ──
  display?: Record<string, unknown> // DisplayFormat shape (kept loose; passed through)
  pivotAgg?: string  // PIVOT_AGG (client-side pivot aggregation), legacy WINDOWFUNCTION
  pivotAxis?: string // PIVOT_AXIS: positional direction (rows | columns)
  width?: number     // WIDTH when numeric (pixel column width)
}

export interface MappedData {
  schema: Record<string, { type?: string }>
  rows: Array<Record<string, unknown>>
  groups: Record<string, MappedColumn[]> // keyed by lowercased StandardName
}

export function mapVisualData(jsonData: VisualJsonData, visualLayout: VisualLayout): MappedData {
  const schema = (jsonData?.[0] ?? {}) as Record<string, { type?: string }>
  const rows = (jsonData?.slice(1) ?? []) as Array<Record<string, unknown>>
  const groups: Record<string, MappedColumn[]> = {}
  for (const g of dropMovedColumnCopies((visualLayout?.ColumnGroups ?? []) as GroupLike[])) {
    const sn = String((g as { StandardName?: unknown }).StandardName ?? '').toLowerCase()
    if (!sn) continue
    const columns = (g as { COLUMNS?: Array<Record<string, unknown>> }).COLUMNS ?? []
    const mapped: MappedColumn[] = []
    for (const c of columns) {
      const name = (c.NAME ?? c.name) as string | undefined
      if (name) mapped.push(toMappedColumn(c, name, schema))
    }
    groups[sn] = mapped
  }
  return { schema, rows, groups }
}

/** Build a rich MappedColumn from a raw ColumnGroup COLUMNS entry. */
export function toMappedColumn(
  c: Record<string, unknown>,
  name: string,
  schema: Record<string, { type?: string }>,
): MappedColumn {
  const str = (v: unknown) => (v == null ? undefined : String(v))
  // HIDDEN is 'F' (false/visible) in some models, boolean true in others.
  const rawHidden = c.HIDDEN
  const hidden =
    rawHidden === true ||
    (typeof rawHidden === 'string' && rawHidden.toLowerCase() === 'true')
  const rawWidth = Number(c.WIDTH)
  const display =
    c.display && typeof c.display === 'object'
      ? (c.display as Record<string, unknown>)
      : undefined
  return {
    name,
    // A BLANK content/label is as unusable as a missing one — a grid header
    // must never render empty, so both fall through to the humanized name.
    label: firstFilled(c.content, c.label) ?? humanizeColumnLabel(String(c.NAME ?? name)),
    type: schema[name]?.type,
    coltype: str(c.COLTYPE ?? c.col_type),
    format: str(c.FORMAT),
    pattern: str(c.PATTERN ?? c.pattern),
    agg: str(c.aggFunction ?? c.FUNCTION ?? c.WINDOWFUNCTION ?? c.DEFAULTFUNCTION),
    hidden,
    alignment: str(c.alignment ?? (c.ALIGNMENT === 2 ? 'right' : undefined)),
    advFormat: str(c.ADV_FORMAT),
    calcExpression: str(c.expression),
    isCalc: c.CALC_TYPE === 'Presentation',
    display,
    pivotAgg: str(c.PIVOT_AGG ?? c.WINDOWFUNCTION),
    pivotAxis: str(c.PIVOT_AXIS),
    width: Number.isFinite(rawWidth) && rawWidth > 0 ? rawWidth : undefined,
  }
}

/** First non-empty group among the given StandardNames (lowercased). */
export function pickGroup(md: MappedData, ...names: string[]): MappedColumn[] {
  for (const n of names) {
    const g = md.groups[n.toLowerCase()]
    if (g && g.length) return g
  }
  return []
}

function schemaColumns(md: MappedData): MappedColumn[] {
  return Object.keys(md.schema)
    .filter((k) => k !== 'id')
    .map((k) => ({ name: k, label: humanizeColumnLabel(k), type: md.schema[k]?.type }))
}

/** Dimension columns: the 'rows' group, else non-numeric schema columns. */
export function dimensions(md: MappedData): MappedColumn[] {
  const g = pickGroup(md, 'rows', 'columns', 'category', 'dimension')
  if (g.length) return g
  return schemaColumns(md).filter((c) => c.type !== 'number')
}

/** Measure columns: the 'values' group, else numeric schema columns. */
export function measures(md: MappedData): MappedColumn[] {
  const g = pickGroup(md, 'values', 'measures', 'measure')
  if (g.length) return g
  return schemaColumns(md).filter((c) => c.type === 'number')
}

export function toNumber(v: unknown): number {
  if (typeof v === 'number') return v
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

export function formatValue(v: unknown, type?: string): string {
  if (v == null) return ''
  if (type === 'number' && (typeof v === 'number' || !Number.isNaN(Number(v)))) {
    return Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 })
  }
  if (type === 'datetime') {
    const d = new Date(String(v))
    if (!Number.isNaN(d.getTime())) {
      return d.getHours() || d.getMinutes() ? d.toLocaleString() : d.toLocaleDateString()
    }
  }
  return String(v)
}
